import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Drawer, Modal, Row, Space, Tag } from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "antd/lib/form/Form";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import { allPermissions } from "../../shared/constants/constants";
import { RootState } from "../../shared/types/common";
import { accessAllowed, showNotification } from "../../shared/utils/utils";
import {
  deleteUser,
  getRoles,
  getUsersList,
} from "../../core/store/reducers/user.slice";
import CreateEditUserComponent from "./components/create-edit-user.component";

const USER_FILTER_LABELS: Record<string, string> = {
  search: "Search",
  roleId: "Role",
  status: "Status",
};

function userFilterValueText(key: string, value: any, roles: any[]): string {
  switch (key) {
    case "roleId":
      return roles.find((role) => role.id === value)?.name ?? String(value);
    case "status":
      return value === true ? "ACTIVE" : "INACTIVE";
    default:
      return String(value).trim();
  }
}

const USER_FILTER_CLEAR: Record<string, any> = {
  search: undefined,
  roleId: undefined,
  status: undefined,
};

const ManageUsersPage: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const { loadingUsers, userList, roleList, pagination } = useSelector(
    (state: RootState) => state.userManagement
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const { screenType } = useSelector((state: RootState) => state.settings);
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null as any);
  const [filterVisible, setFilterVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState("");
  const [openLogs, setOpenLogs] = useState(false);
  const [filters, setFilters] = useState({} as any);
  const [userListData, setUserListData] = useState([] as any);
  const [pageSize, setPageSize] = useState(TABLE_ROWS_COUNT);

  useEffect(() => {
    dispatch(getRoles());
  }, [dispatch]);

  useEffect(() => {
    loadList(1);
  }, [filters, pageSize]);

  useEffect(() => {
    setUserListData(
      userList.map((obj: any, index: number) => ({ key: index, ...obj }))
    );
  }, [userList]);

  const loadList = (page: any = null) => {
    const params = {
      page: page ? page : pagination?.page ? pagination?.page : 1,
      limit: pageSize,
    };
    const body = {} as any;
    Object.keys(filters)?.map((key: any) => {
      if (isFilterValueActive(filters[key])) body[key] = filters[key];
    });
    dispatch(getUsersList({ params, body }));
  };

  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };

  const editEntityListner = (entity: any) => {
    setCreateEditOpen(true);
    setSelectedEntity(entity);
  };

  const handleDeleteModal = (userId: string) => {
    setDeleteModalVisible(true);
    setUserIdToDelete(userId);
  };

  const handleDelete = (userId: string) => {
    dispatch(deleteUser(userId)).then((val: any) => {
      if (val?.payload?.data) {
        showNotification("User Deleted Successfully", "success");
        loadList();
        setDeleteModalVisible(false);
        setUserIdToDelete("");
      } else if (val?.payload?.message) {
        showNotification(val.payload.message, "error");
      }
    });
  };

  const handleOpenLogs = (record: any) => {
    setOpenLogs(true);
    setSelectedEntity(record);
  };

  const onDeleteConfirm = () => {
    handleDelete(userIdToDelete);
  };

  const onDeleteCancel = () => {
    setDeleteModalVisible(false);
    setUserIdToDelete("");
  };

  const createEditSubmit = () => {
    setCreateEditOpen(false);
    loadList();
  };

  const onPageChange = (pageNumber: number) => {
    loadList(pageNumber);
  };
  const onPageSizeChange = (_pageNumber: number, selectedPageSize: number) => {
    setPageSize(selectedPageSize);
  };

  const onFilterApply = (values: any) => {
    setFilters(values);
  };

  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      form.setFieldsValue({ [key]: USER_FILTER_CLEAR[key] });
    },
    [form]
  );

  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((key) => isFilterValueActive(filters[key]))
      .map((key) => ({
        key,
        fieldLabel: USER_FILTER_LABELS[key] || key,
        valueText: userFilterValueText(key, filters[key], roleList),
      }));
  }, [filters, roleList]);

  const roleOptions = roleList.map((role: any) => ({
    value: role?.id,
    label: role?.name,
  }));

  const hasAnyUserAccess =
    accessAllowed(myPermissions, allPermissions.UPDATE_USER) ||
    accessAllowed(myPermissions, allPermissions.DELETE_USER) ||
    accessAllowed(myPermissions, allPermissions.USER_ACTIVITY_LOGS);

  const columns = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role: any) => role?.name || "",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: boolean) => (
        <Tag color={status ? "green" : "default"}>{status ? "ACTIVE" : "INACTIVE"}</Tag>
      ),
    },
    ...(hasAnyUserAccess
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            render: (_text: any, record: any) => (
              <Space>
                {accessAllowed(myPermissions, allPermissions.UPDATE_USER) && (
                  <Button
                    type="link"
                    onClick={() => editEntityListner(record)}
                    icon={<EditOutlined />}
                  />
                )}
                {accessAllowed(myPermissions, allPermissions.DELETE_USER) && (
                  <Button
                    type="link"
                    onClick={() => handleDeleteModal(record?.id)}
                    icon={<DeleteOutlined style={{ color: "#FF0000" }} />}
                  />
                )}
                {accessAllowed(myPermissions, allPermissions.USER_ACTIVITY_LOGS) && (
                  <Button
                    type="link"
                    onClick={() => handleOpenLogs(record)}
                    icon={<EyeOutlined />}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  const formProps = {
    form,
    name: "manage-users-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "search",
        label: "",
        placeholder: "Name or Email Search",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "roleId",
        label: "",
        placeholder: "Role",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        options: roleOptions,
      },
      {
        name: "status",
        label: "",
        placeholder: "Status",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        allowClear: true,
        options: [
          { value: true, label: "ACTIVE" },
          { value: false, label: "INACTIVE" },
        ],
      },
    ],
    initialValues: {},
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
        onClick: clearAllFilters,
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: () => {
      return;
    },
  };

  const headerButtons = (
    <Space size="middle" className="app-page-actions">
      {accessAllowed(myPermissions, allPermissions.CREATE_USER) && (
        <Button type="primary" icon={<PlusOutlined />} onClick={createEntityListner}>
          <span className="font-size-14">New {!screenType?.isPortrait && "User"}</span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() => setFilterVisible(!filterVisible)}
        title={filterVisible ? "Hide filters" : "Show filters"}
      />
    </Space>
  );

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <UserOutlined />
              </div>
              <h1 className="app-page-title">Manage Users</h1>
            </div>
            <p className="app-page-subtitle">View and manage login users</p>
          </Col>
          <Col>{headerButtons}</Col>
        </Row>
      </div>

      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-header">
            <div className="app-filter-title">
              <BiFilterAlt style={{ fontSize: 14 }} />
              Filters
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setFilterVisible(false)}
              title="Close filters"
              aria-label="Close filters"
            />
          </div>
          <div className="table-filter-form">
            <GenericFormComponent {...formProps} />
          </div>
        </div>
      )}

      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      <div className="app-table-card">
        <DataTableComponent
          rowKey="id"
          pagination={true}
          loading={loadingUsers}
          dataSource={userListData}
          columns={columns}
          defaultPage={0}
          currentPage={pagination?.page || 1}
          totalRows={pagination?.total || 0}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSize={pageSize}
        />
      </div>

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={onDeleteConfirm}
        onCancel={onDeleteCancel}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this user?</p>
      </Modal>

      {createEditOpen && (
        <CreateEditUserComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}

      {openLogs && (
        <Drawer open={openLogs} onClose={() => setOpenLogs(false)} title="Log Details">
          <ActivityLogComponent
            id={selectedEntity?.id}
            record={selectedEntity}
            module="User"
          />
        </Drawer>
      )}
    </div>
  );
};

export default ManageUsersPage;


